import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Users, FileText, CalendarDays, FolderOpen, HardHat,
  LogOut, Shield, Settings, User, X,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { BUILD_VERSION } from '@/version';
import gpsLogo from '@/GPS-logo-2022.svg';

// Nav-Einträge mit zugehörigem Modul; deaktivierte Module werden
// herausgefiltert. "Meine Anträge" gehört zum Konto, nicht zu einem Modul.
const navigation = [
  { name: 'Kontakte', href: '/', icon: Users, module: 'kontakte' },
  { name: 'Kalender', href: '/calendar', icon: CalendarDays, module: 'kalender' },
  { name: 'Dokumente', href: '/dokumente', icon: FolderOpen, module: 'dokumente' },
  { name: 'Bauvorhaben', href: '/bauvorhaben', icon: HardHat, module: 'bauvorhaben' },
  { name: 'Meine Anträge', href: '/proposals', icon: FileText, module: null as string | null },
];

interface SidebarProps {
  variant: 'rail' | 'drawer';
  onNavigate?: () => void;
}

/**
 * Linke Menüleiste: ein Component, zwei Varianten — rail (docked ≥ md)
 * und drawer (Overlay < md). Enthält die Modul-gefilterte Navigation,
 * Admin-Sektion, Profil, Abmelden und die Build-Version.
 */
export function Sidebar({ variant, onNavigate }: SidebarProps) {
  const { user, logout, isAdmin, isApprover, hasModule } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const items = navigation.filter((item) => !item.module || hasModule(item.module));

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/' || location.pathname.startsWith('/contacts/');
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    onNavigate?.();
    logout();
  };

  const handleNavigate = (path: string) => {
    onNavigate?.();
    navigate(path);
  };

  return (
    <aside
      className={
        variant === 'rail'
          ? 'hidden md:flex w-56 shrink-0 flex-col border-r border-gray-200 bg-white'
          : 'md:hidden fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-white shadow-xl'
      }
    >
      {/* Logo (+ Schließen-Button im Drawer) */}
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-100 px-4 pt-[env(safe-area-inset-top)]">
        <Link to="/" className="flex items-center focus:outline-none" onClick={onNavigate}>
          <img src={gpsLogo} alt="GPS" className="h-8 w-auto" />
        </Link>
        {variant === 'drawer' && (
          <button
            type="button"
            onClick={onNavigate}
            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title="Menü schließen"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-1 px-2">
          {items.map((item) => (
            <li key={item.name}>
              <Link
                to={item.href}
                onClick={onNavigate}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium ${
                  isActive(item.href)
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <item.icon className={`h-4 w-4 shrink-0 ${isActive(item.href) ? 'text-primary-600' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            </li>
          ))}
        </ul>

        {/* Admin-Sektion (approver+) */}
        {isApprover && (
          <div className="mt-6 border-t border-gray-100 pt-3">
            <p className="px-5 pb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Administration
            </p>
            <ul className="space-y-1 px-2">
              <li>
                <button
                  type="button"
                  onClick={() => handleNavigate('/admin')}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium ${
                    location.pathname.startsWith('/admin')
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Shield className="h-4 w-4 shrink-0 text-gray-400" />
                  Benutzerverwaltung
                </button>
              </li>
              {isAdmin && (
                <li>
                  <button
                    type="button"
                    onClick={() => handleNavigate('/admin?tab=settings')}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <Settings className="h-4 w-4 shrink-0 text-gray-400" />
                    Systemeinstellungen
                  </button>
                </li>
              )}
              <li>
                <button
                  type="button"
                  onClick={() => handleNavigate('/profile')}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium ${
                    location.pathname.startsWith('/profile')
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <User className="h-4 w-4 shrink-0 text-gray-400" />
                  Profil
                </button>
              </li>
            </ul>
          </div>
        )}
      </nav>

      {/* unten: Benutzerblock + Abmelden + Version */}
      <div className="shrink-0 border-t border-gray-100 p-3">
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="h-8 w-8 shrink-0 rounded-full bg-primary-100 flex items-center justify-center">
            <span className="text-primary-600 font-medium text-sm">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-gray-900">{user?.name}</p>
            <span
              className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                user?.role === 'admin'
                  ? 'bg-purple-100 text-purple-800'
                  : user?.role === 'approver'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
              }`}
            >
              {user?.role === 'admin' ? 'Administrator' : user?.role === 'approver' ? 'Genehmiger' : 'Benutzer'}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="mt-1 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4" />
          Abmelden
        </button>
        <p className="select-none pb-1 pt-2 text-center text-[10px] text-gray-400">v{BUILD_VERSION}</p>
      </div>
    </aside>
  );
}